import type { AdminSecuritySettings, MyMfaSetupResponse } from '@inflip/contracts/admin';
import { useCallback, useEffect, useState } from 'react';

import { OtpEnrollment } from '../components/otp-enrollment';
import {
  Button,
  Dialog,
  DialogFooter,
  ErrorBanner,
  InfoRow,
  LoadingRow,
  PageHeader,
  Section,
  inputCls,
} from '../components/ui';
import { api } from '../lib/api-client';
import { useAdminAuth } from '../lib/auth-context';
import { showToast } from '../lib/toast';

const MY_MFA_LABEL: Record<AdminSecuritySettings['myMfa'], string> = {
  ENROLLED: '등록됨',
  PENDING: '등록 대기 (첫 코드 확인 전)',
  NONE: '미등록',
};

/** 6자리 숫자만 남긴다 — 붙여 넣은 공백·하이픈 등 */
function otpDigits(value: string): string {
  return value.replace(/\D/g, '').slice(0, 6);
}

/**
 * 보안 설정 — 관리자 2단계 OTP 인증 켜기·끄기(슈퍼어드민) + 내 OTP 등록.
 * 켜면 OTP 없이 로그인한 세션(지금 세션 포함)은 막히므로, 켠 뒤 바로 로그아웃시켜 OTP 로 다시 로그인하게 한다.
 */
export function SecuritySettingsPage() {
  const { session, signOutWithNotice } = useAdminAuth();
  const [settings, setSettings] = useState<AdminSecuritySettings | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [toggleTo, setToggleTo] = useState<boolean | null>(null);
  const [setup, setSetup] = useState<MyMfaSetupResponse | null>(null);
  const [setupCode, setSetupCode] = useState('');
  const [pending, setPending] = useState(false);

  const load = useCallback(() => {
    if (!session) return Promise.resolve();
    return api
      .getSecuritySettings(session.accessToken)
      .then(setSettings)
      .catch((err) => setError(err instanceof Error ? err.message : String(err)));
  }, [session]);

  useEffect(() => {
    void load();
  }, [load]);

  if (!session) return null;
  const token = session.accessToken;

  async function startSetup() {
    if (pending) return;
    setPending(true);
    setError(null);
    try {
      setSetup(await api.setupMyMfa(token));
      setSetupCode('');
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      await load();
    } finally {
      setPending(false);
    }
  }

  async function confirmSetup(e: React.FormEvent) {
    e.preventDefault();
    if (pending || setupCode.length !== 6) return;
    setPending(true);
    setError(null);
    try {
      setSettings(await api.confirmMyMfa(token, { code: setupCode }));
      setSetup(null);
      showToast('OTP 를 등록했어요');
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setPending(false);
    }
  }

  async function saveToggle(enabled: boolean, code: string) {
    if (pending) return;
    setPending(true);
    setError(null);
    try {
      const next = await api.updateAdminMfaSetting(token, { enabled, code });
      setToggleTo(null);
      if (enabled) {
        // 지금 세션은 OTP 없이 로그인한 세션이라 곧바로 막힌다 — 안내와 함께 다시 로그인
        signOutWithNotice('관리자 2단계 OTP 인증을 켰어요. OTP 코드를 넣어 다시 로그인해 주세요.');
        return;
      }
      setSettings(next);
      showToast('관리자 2단계 OTP 인증을 껐어요');
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      await load();
    } finally {
      setPending(false);
    }
  }

  return (
    <main className="mx-auto w-full max-w-xl px-6 py-8">
      <PageHeader
        title="보안 설정"
        description="관리자 로그인에 OTP 앱의 6자리 코드를 함께 받을지 정하고, 내 OTP 를 등록해요"
      />
      {error && <ErrorBanner message={error} />}
      {!settings && !error && <LoadingRow />}

      {settings && (
        <>
          <Section
            title="관리자 2단계 OTP 인증"
            action={
              settings.canManage &&
              !settings.adminMfaForcedByServer &&
              settings.myMfa === 'ENROLLED' && (
                <Button
                  size="sm"
                  variant={settings.adminMfaSaved ? 'danger' : 'primary'}
                  disabled={pending}
                  onClick={() => setToggleTo(!settings.adminMfaSaved)}
                >
                  {settings.adminMfaSaved ? '끄기' : '켜기'}
                </Button>
              )
            }
            divided
          >
            <InfoRow label="지금 상태">
              {settings.adminMfaEnabled ? (
                <span className="text-emerald-600">켜짐</span>
              ) : (
                <span className="text-muted-foreground">꺼짐</span>
              )}
              {settings.adminMfaForcedByServer && (
                <span className="ml-1.5 text-xs text-muted-foreground">
                  서버 설정으로 항상 켜짐
                </span>
              )}
            </InfoRow>
            <p className="py-3 text-xs leading-relaxed text-muted-foreground">
              켜면 모든 관리자가 로그인할 때 비밀번호와 함께 OTP 코드를 넣어야 해요. OTP 를 아직
              등록하지 않은 관리자는 다음 로그인에서 등록해요. 켜기·끄기 모두 내 OTP 의 지금 코드로
              확인하고, 켜면 OTP 없이 로그인한 세션은 모두 다시 로그인해야 해요.
              {!settings.canManage && ' 슈퍼어드민만 바꿀 수 있어요.'}
              {settings.canManage &&
                !settings.adminMfaForcedByServer &&
                settings.myMfa !== 'ENROLLED' &&
                ' 먼저 아래에서 내 OTP 를 등록해야 켤 수 있어요.'}
            </p>
          </Section>

          <Section title="내 OTP" divided>
            <InfoRow label="등록 상태">
              <span className={settings.myMfa === 'ENROLLED' ? 'text-emerald-600' : undefined}>
                {MY_MFA_LABEL[settings.myMfa]}
              </span>
            </InfoRow>
            {settings.myMfa === 'ENROLLED' ? (
              <p className="py-3 text-xs leading-relaxed text-muted-foreground">
                휴대폰을 바꾸거나 잃어버렸다면 슈퍼어드민에게 어드민 계정 화면의 [OTP 초기화]를
                요청해 주세요. 다음 로그인에서 다시 등록해요.
              </p>
            ) : setup ? (
              <form onSubmit={confirmSetup} className="space-y-3 py-3">
                <OtpEnrollment secret={setup.secret} otpauthUrl={setup.otpauthUrl} />
                <div className="flex gap-2">
                  <input
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    placeholder="앱에 보이는 6자리 코드"
                    value={setupCode}
                    onChange={(e) => setSetupCode(otpDigits(e.target.value))}
                    className={`min-w-0 flex-1 ${inputCls}`}
                  />
                  <Button type="submit" disabled={pending || setupCode.length !== 6}>
                    등록 확인
                  </Button>
                </div>
              </form>
            ) : (
              <div className="flex items-center justify-between gap-3 py-3">
                <p className="text-xs text-muted-foreground">
                  OTP 앱(Google Authenticator 등)에 이 계정을 등록해요.
                </p>
                <Button size="sm" disabled={pending} onClick={() => void startSetup()}>
                  {settings.myMfa === 'PENDING' ? '등록 이어서 하기' : 'OTP 등록'}
                </Button>
              </div>
            )}
          </Section>
        </>
      )}

      {toggleTo !== null && (
        <OtpConfirmDialog
          enabling={toggleTo}
          busy={pending}
          onSubmit={(code) => void saveToggle(toggleTo, code)}
          onClose={() => setToggleTo(null)}
        />
      )}
    </main>
  );
}

/** 켜기·끄기 확인 — 내 OTP 의 지금 코드 */
function OtpConfirmDialog({
  enabling,
  busy,
  onSubmit,
  onClose,
}: {
  enabling: boolean;
  busy: boolean;
  onSubmit: (code: string) => void;
  onClose: () => void;
}) {
  const [code, setCode] = useState('');
  const title = enabling ? '2단계 OTP 인증을 켤까요?' : '2단계 OTP 인증을 끌까요?';
  const ready = code.length === 6 && !busy;
  return (
    <Dialog label={title} title={title} onClose={onClose}>
      <p className="text-sm text-muted-foreground">
        {enabling
          ? '켜면 지금 로그인도 끝나요. OTP 코드를 넣어 다시 로그인해 주세요.'
          : '끄면 관리자 로그인에 비밀번호만 받아요.'}{' '}
        확인을 위해 내 OTP 앱의 지금 코드를 넣어 주세요.
      </p>
      <input
        inputMode="numeric"
        autoComplete="one-time-code"
        placeholder="6자리 코드"
        value={code}
        onChange={(e) => setCode(otpDigits(e.target.value))}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && ready) onSubmit(code);
        }}
        className={`mt-4 w-full ${inputCls}`}
        autoFocus
      />
      <DialogFooter>
        <Button variant="secondary" className="flex-1" onClick={onClose}>
          취소
        </Button>
        <Button
          variant={enabling ? 'primary' : 'danger'}
          className="flex-1"
          disabled={!ready}
          onClick={() => onSubmit(code)}
        >
          {enabling ? '켜기' : '끄기'}
        </Button>
      </DialogFooter>
    </Dialog>
  );
}
